"use client"

import { graphql } from "@/gql"
import { useQuery } from "@apollo/client"
import { Spinner, Text } from "@/elements"
import { NotificationItem } from "./NotificationItem"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import { useEffect, useRef, useState } from "react"
import { NotificationSkeleton } from "./NotificationItemSkeleton"
import { useNotifications } from "./useNotifications"
import { GetMyNotificationsFeedDocument } from "@/gql/graphql"
import type { NovaSession } from "@/types"

graphql(`
  query GetMyNotificationsFeed($first: Int, $after: String, $id: ID!) {
    node(id: $id) {
      id
      __typename
      ... on User {
        notificationsFeed(first: $first, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              __typename
              ...NotificationItem
            }
          }
        }
      }
    }
  }
`)

interface Props {
  serverSession: NovaSession
  isOpen: boolean
}

export type SeenNotification = {
  id: string
  group: string
  isSeen: boolean
}

const PAGE_SIZE = 10

export const Notifications: React.FC<Props> = ({ serverSession, isOpen }) => {
  const loadMoreRef = useRef(null)
  const [seenNotifications, setSeenNotifications] = useState<SeenNotification[]>([])
  const { handleViewableItemsChanged } = useNotifications()

  const handleView = (notification: SeenNotification) => {
    const seenIds = seenNotifications.map(({ id }) => id)
    if (!seenIds.includes(notification.id)) {
      setSeenNotifications(prevSeen => [...prevSeen, notification])
    }
  }

  useEffect(() => {
    let wasOpen = false

    // Track when `isOpen` goes from true to false
    if (isOpen && !wasOpen) {
      wasOpen = true
    }

    if (!isOpen && wasOpen) {
      handleViewableItemsChanged(seenNotifications)
      wasOpen = false // Reset for the next open-close cycle
    }

    // Cleanup function to handle component unmount
    return () => {
      if (wasOpen) {
        handleViewableItemsChanged(seenNotifications)
      }
    }
  }, [isOpen, seenNotifications, handleViewableItemsChanged])

  const { data, fetchMore, networkStatus } = useQuery(GetMyNotificationsFeedDocument, {
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    variables: {
      first: PAGE_SIZE,
      after: null,
      id: serverSession.user.id,
    },
    skip: !isOpen,
  })

  const user = data?.node?.__typename === "User" ? data.node : null

  useHandleListEndReached({
    pageInfo: user?.notificationsFeed?.pageInfo,
    first: PAGE_SIZE,
    networkStatus,
    fetchMore,
    observerRef: loadMoreRef,
  })

  return (
    <div
      className="flex flex-col px-2 py-4"
      onMouseEnter={() => {
        document.body.style.overflow = "hidden"
      }}
      onMouseLeave={() => {
        document.body.style.overflow = "auto"
      }}>
      <div className="px-2">
        <Text variant="h5">Notifications</Text>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {!!data
          ? user?.notificationsFeed?.edges.map(edge => {
              if (!edge?.node) return null
              return (
                <NotificationItem
                  key={edge?.node?.id}
                  item={edge?.node}
                  onView={handleView}
                  serverSession={serverSession}
                />
              )
            })
          : Array.from({ length: 10 }).map((_, index) => <NotificationSkeleton key={index} />)}
      </div>

      <div className="relative mt-2 flex h-[20px] w-full flex-row items-center justify-center">
        {user?.notificationsFeed?.pageInfo?.hasNextPage && <Spinner size="medium" className="bg-fg-2" />}
      </div>

      <div ref={loadMoreRef} className="h-[5px]" />
    </div>
  )
}
